
import gql from 'graphql-tag'
import { CreateAndEndActivePostingMutationVariables, useCreateAndEndActivePostingMutation, useValidatePostingCreateLazyQuery, usePostingReplaceExistingMutation, PostingValidationResult } from '../../generated/graphql'
import useForm from 'react-hook-form'
import propName from "../../helpers/propName"
import * as yup from "yup"
import React, { useState } from 'react'
import HookedTextInput from '../../components/General/Inputs/HookedTextInput'
import HookedDateTime from '../../components/General/Inputs/HookedDateTime'

import SupplierSearch from '../../components/Search/SupplierSearch/SupplierSearch'
import TerminalSearch from '../../components/Search/TerminalSearch/TerminalSearch'
import FormPage from '../../components/General/FormPage/FormPage'
import FormTile from '../../components/Tiles/FormTile/FormTile'
import Button, { ButtonType } from '../../components/General/Button/Button'
import moment from 'moment'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { showSuccessToast, showErrorToast } from '../../utils/errorUtils'
import PostingConflictDialog from '../../components/Dialogs/PostingConflictDialog'
import RelatedPostings from '../../components/Postings/RelatedPostings'

export const CreatePosting = gql`
    mutation CreatePosting($posting: PostingInput!) {
        postingCreate(posting: $posting) {
            postingKey
        }
    }
`

export const ValidatePostingCreate = gql`
    query ValidatePostingCreate($supplierKey: Int!, $terminalKey: Int!, $effectiveDate: DateTime!) {
        postingValidateCreate(supplierKey: $supplierKey, terminalKey: $terminalKey, effectiveDate: $effectiveDate) {
            isValid
            errorMessage
            validationType
            suggestedResolution
            canAutoResolve
            conflictingPostings {
                postingKey
                cost
                effectiveDate
                supplier {
                    name
                }
                terminal {
                    name
                }
            }
        }
    }
`

export const GetRelatedPostings = gql`
    query GetRelatedPostings($supplierKey: Int!, $terminalKey: Int!) {
        postingGetAll(
            where: { 
                and: [
                    { supplierKey: { eq: $supplierKey } }
                    { terminalKey: { eq: $terminalKey } }
                ]
            }
            order: { effectiveDate: DESC }
            first: 5
        ) {
            nodes {
                postingKey
                cost
                effectiveDate
                supplier {
                    name
                }
                terminal {
                    name
                }
            }
        }
    }
`

export const PostingReplaceExisting = gql`
    mutation PostingReplaceExisting($posting: PostingInput!, $existingPostingKey: Int!) {
        postingReplaceExisting(posting: $posting, existingPostingKey: $existingPostingKey) {
            postingKey
        }
    }
`

interface Props {
}

const PostingCreate = (props: Props) => {
    const navigate = useNavigate()

    const [createPostingMutation] = useCreateAndEndActivePostingMutation()
    const [replacePostingMutation] = usePostingReplaceExistingMutation()
    const [validatePosting] = useValidatePostingCreateLazyQuery()

    const [startDate, setStartDate] = useState<Date>(moment().startOf('day').toDate())
    let oneYearFromStart = moment().startOf('day').add(1, 'year').toDate()

    const [endDate, setEndDate] = useState<Date>(oneYearFromStart)
    const [conflictDialogOpen, setConflictDialogOpen] = useState(false)
    const [validationResult, setValidationResult] = useState<PostingValidationResult | null>(null)
    const [pendingSubmission, setPendingSubmission] = useState<CreateAndEndActivePostingMutationVariables | null>(null)
    const [selectedSupplierKey, setSelectedSupplierKey] = useState<number | null>(null)
    const [selectedTerminalKey, setSelectedTerminalKey] = useState<number | null>(null)

    const [searchParams,] = useSearchParams()
    const supplierKey = searchParams.get("supplierKey") ? parseInt(searchParams.get("supplierKey")) : 0

    const navigateToList = () => {
        navigate("/settings/postings")
    }

    const navigateToPosting = (id: number) => {
        navigate(`/settings/postings/edit/${id}`)
    }

    const submit = async (variables: CreateAndEndActivePostingMutationVariables) => {
        // Use state values for supplier and terminal keys
        const submissionVariables = {
            ...variables,
            posting: {
                ...variables.posting,
                supplierKey: selectedSupplierKey,
                terminalKey: selectedTerminalKey
            }
        };

        // First validate the posting for conflicts
        try {
            const validationResponse = await validatePosting({
                variables: {
                    supplierKey: submissionVariables.posting.supplierKey,
                    terminalKey: submissionVariables.posting.terminalKey,
                    effectiveDate: submissionVariables.posting.effectiveDate
                }
            });

            const validation = validationResponse.data?.postingValidateCreate;

            if (!validation?.isValid && validation?.validationType === 'EXACT_DUPLICATE') {
                // Show conflict dialog for exact duplicates
                setValidationResult(validation as PostingValidationResult);
                setPendingSubmission(submissionVariables);
                setConflictDialogOpen(true);
                return;
            } else if (!validation?.isValid) {
                // For other validation errors, show error message
                console.error('Validation failed:', validation?.errorMessage);
                return;
            }

            // If validation passes, proceed with creation
            await createPosting(submissionVariables);
        } catch (error) {
            // If validation query fails, proceed with creation (fallback)
            console.warn('Validation query failed, proceeding with creation:', error);
            await createPosting(submissionVariables);
        }
    };

    const createPosting = async (variables: CreateAndEndActivePostingMutationVariables) => {
        try {
            // Add far future end date for database compatibility
            const postingWithEndDate = {
                ...variables.posting,
                effectiveEndDate: new Date(2099, 11, 31) // December 31, 2099
            };

            const result = await createPostingMutation({
                variables: {
                    posting: postingWithEndDate
                }
            });
            showSuccessToast("Posting created successfully");
            navigateToPosting(result.data.postingCreateAndEndActive.postingKey);
        } catch (error) {
            // Let the global Apollo error handler show the toast
            console.error('Create posting failed:', error);
        }
    };

    const handleConflictResolution = async (resolution: 'replace' | 'edit' | 'cancel') => {
        setConflictDialogOpen(false);

        if (!pendingSubmission || !validationResult) {
            return;
        }

        switch (resolution) {
            case 'replace':
                // Replace the existing posting with the new one
                if (validationResult.conflictingPostings?.[0]?.postingKey) {
                    try {
                        // Add far future end date for database compatibility
                        const postingWithEndDate = {
                            ...pendingSubmission.posting,
                            effectiveEndDate: new Date(2099, 11, 31) // December 31, 2099
                        };

                        const result = await replacePostingMutation({
                            variables: {
                                posting: postingWithEndDate,
                                existingPostingKey: validationResult.conflictingPostings[0].postingKey
                            }
                        });
                        showSuccessToast("Posting replaced successfully");
                        navigateToPosting(result.data.postingReplaceExisting.postingKey);
                    } catch (error) {
                        console.error('Replace posting failed:', error);
                    }
                }
                break;

            case 'edit':
                // Navigate to edit the existing posting
                if (validationResult.conflictingPostings?.[0]?.postingKey) {
                    navigateToPosting(validationResult.conflictingPostings[0].postingKey);
                }
                break;

            case 'cancel':
                // Do nothing, just close the dialog
                break;
        }

        setPendingSubmission(null);
        setValidationResult(null);
    };

    const handleSupplierChange = (supplierKey: number | null) => {
        setSelectedSupplierKey(supplierKey);
    };

    const handleTerminalChange = (terminalKey: number | null) => {
        setSelectedTerminalKey(terminalKey);
    };

    const startDateChanged = (key, val) => {
        setStartDate(val)
        //when creating a new index contract, I figure it would be nice for the end date to update based on a change to the start date. End date can then be adjusted manually.
        setEndDate(moment(val).add(1, 'year').subtract(1, 'day').toDate())
        setValue(key, val)
    }

    const endDateChanged = (key, val) => {
        setEndDate(val)
        setValue(key, val)
    }

    const validationSchema = yup.object().shape({
        posting: yup.object().shape({
            cost: yup.number().required(),
            effectiveDate: yup.date().required(),

            supplierKey: yup.number().required(),
            terminalKey: yup.number().required()
        })
    })

    const { register, handleSubmit, errors, setValue } = useForm<CreateAndEndActivePostingMutationVariables>({ validationSchema })

    // Effect to handle prefilled supplier from URL
    React.useEffect(() => {
        if (supplierKey !== 0 && selectedSupplierKey !== supplierKey) {
            setSelectedSupplierKey(supplierKey);
        }
    }, [supplierKey]);

    const onSubmit = handleSubmit(submit)
    const commonProps = { register, errors, setValue }

    const footerLeftContent = (<Button clickAction={navigateToList} buttonType={ButtonType.Secondary}>Cancel</Button>)
    const footerRightContent = (<Button clickAction={onSubmit} buttonType={ButtonType.Success}>Create Posting</Button>)

    return (
        <>
            <FormPage title="Create Posting" footerLeftContent={footerLeftContent} footerRightContent={footerRightContent}>
                <form onSubmit={onSubmit}>
                    <FormTile>
                        <div className="row">
                            <div className="col-md-6">
                                <SupplierSearch
                                    label="Supplier"
                                    propertyKey={propName<CreateAndEndActivePostingMutationVariables>(o => o.posting.supplierKey)}
                                    prefilledSupplierKey={supplierKey !== 0 ? supplierKey : undefined}
                                    onSelect={(supplier) => handleSupplierChange(supplier?.supplierKey || null)}
                                    {...commonProps}
                                />
                            </div>
                            <div className="col-md-6">
                                <TerminalSearch
                                    label="Terminal"
                                    propertyKey={propName<CreateAndEndActivePostingMutationVariables>(o => o.posting.terminalKey)}
                                    onSelect={(terminal) => handleTerminalChange(terminal?.terminalKey || null)}
                                    {...commonProps}
                                />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-md-4">
                                <HookedTextInput
                                    label="Posting"
                                    propertyKey={propName<CreateAndEndActivePostingMutationVariables>(o => o.posting.cost)}
                                    {...commonProps}
                                />
                            </div>
                            <div className="col-md-6">
                                <HookedDateTime
                                    label="Effective Date"
                                    propertyKey={propName<CreateAndEndActivePostingMutationVariables>(o => o.posting.effectiveDate)}
                                    defaultValue={startDate}
                                    setValue={startDateChanged}
                                    showTimeSelect={false}
                                    {...commonProps}
                                />
                            </div>
                        </div>
                    </FormTile>

                    <RelatedPostings
                        supplierKey={selectedSupplierKey}
                        terminalKey={selectedTerminalKey}
                        onPostingClick={navigateToPosting}
                    />
                </form>
            </FormPage>

            <PostingConflictDialog
                isOpen={conflictDialogOpen}
                validation={validationResult}
                onResolve={handleConflictResolution}
            />
        </>
    )
}
export default PostingCreate